import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Spinner } from 'react-bootstrap'
import axiosInstance from '../services/axiosInterceptor'
import OfficialDocLayout from './print/OfficialDocLayout'
import { RESOLUTION_STATUS, fmtDateTHFull, fmtTime } from './aoShared'

// พิมพ์เอกสารการประชุม — mode 'invite' = หนังสือเชิญ+วาระ, 'report' = รายงานการประชุม
export default function MeetingPrint({ mode }) {
  const { mtId } = useParams()
  const [meeting, setMeeting] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    axiosInstance.get(`/api/ao/meetings/${mtId}`)
      .then(res => { if (res.data.success) setMeeting(res.data.data) })
      .catch(err => setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ'))
  }, [mtId])

  if (error) return <div className="p-4"><Alert variant="danger">{error}</Alert></div>
  if (!meeting) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>

  const timeRange = meeting.mt_time_start
    ? `เวลา ${fmtTime(meeting.mt_time_start)}${meeting.mt_time_end ? ` - ${fmtTime(meeting.mt_time_end)}` : ''}`
    : ''

  if (mode === 'invite') {
    return (
      <OfficialDocLayout title="หนังสือเชิญประชุม" backLink={`/admin-office/meetings/${mtId}`}>
        <div style={{ marginTop: 8 }}>เรื่อง&nbsp;&nbsp;ขอเชิญประชุม{meeting.mt_title} ครั้งที่ {meeting.mt_full_no}</div>
        <div>เรียน&nbsp;&nbsp;ครูและบุคลากรที่เกี่ยวข้องทุกท่าน</div>
        <p className="doc-indent" style={{ marginTop: 12 }}>
          ด้วยโรงเรียนกำหนดจัด{meeting.mt_title} ครั้งที่ {meeting.mt_full_no}
          ในวัน{fmtDateTHFull(meeting.mt_date)} {timeRange}
          {meeting.mt_place ? ` ณ ${meeting.mt_place}` : ''} จึงขอเชิญท่านเข้าร่วมประชุมโดยพร้อมเพรียงกัน
          ตามระเบียบวาระการประชุมดังนี้
        </p>
        <div style={{ marginTop: 8, fontWeight: 'bold' }}>ระเบียบวาระการประชุม</div>
        {meeting.agenda.length === 0 ? (
          <div className="text-muted">— ยังไม่ได้กำหนดวาระ —</div>
        ) : (
          meeting.agenda.map(a => (
            <div key={a.ag_id} style={{ marginTop: 4 }}>
              <div>วาระที่ {a.ag_order}&nbsp;&nbsp;{a.ag_title}</div>
              {a.ag_detail && <div style={{ marginLeft: '2.5cm', whiteSpace: 'pre-wrap' }}>{a.ag_detail}</div>}
            </div>
          ))
        )}
        <p className="doc-indent" style={{ marginTop: 12 }}>จึงเรียนมาเพื่อทราบและเข้าร่วมประชุมตามวันเวลาดังกล่าว</p>
        <div className="doc-sign" style={{ marginLeft: '50%' }}>
          <div>ลงชื่อ ................................................</div>
          <div style={{ marginTop: 6 }}>(....................................)</div>
          <div>ผู้อำนวยการโรงเรียน</div>
        </div>
      </OfficialDocLayout>
    )
  }

  // mode 'report' — รายงานการประชุม
  const attended = meeting.invitees.filter(i => i.mi_attend === 1)
  const absent = meeting.invitees.filter(i => i.mi_attend === 0)
  return (
    <OfficialDocLayout title={`รายงานการประชุม ครั้งที่ ${meeting.mt_full_no}`} backLink={`/admin-office/meetings/${mtId}`}>
      <div style={{ textAlign: 'center' }}>{meeting.mt_title}</div>
      <div style={{ textAlign: 'center' }}>
        วัน{fmtDateTHFull(meeting.mt_date)} {timeRange}{meeting.mt_place ? ` ณ ${meeting.mt_place}` : ''}
      </div>

      <div style={{ marginTop: 12, fontWeight: 'bold' }}>ผู้เข้าประชุม ({attended.length} คน)</div>
      <div style={{ columns: 2 }}>
        {attended.map((i, idx) => <div key={i.mi_id}>{idx + 1}. {i.t_name}</div>)}
        {!attended.length && <div className="text-muted">— ยังไม่ได้เช็คชื่อ —</div>}
      </div>
      {absent.length > 0 && (
        <>
          <div style={{ marginTop: 8, fontWeight: 'bold' }}>ผู้ไม่มาประชุม ({absent.length} คน)</div>
          <div style={{ columns: 2 }}>
            {absent.map((i, idx) => <div key={i.mi_id}>{idx + 1}. {i.t_name}</div>)}
          </div>
        </>
      )}

      <div style={{ marginTop: 12, fontWeight: 'bold' }}>ระเบียบวาระและมติที่ประชุม</div>
      {meeting.agenda.map(a => {
        const resolutions = meeting.resolutions.filter(r => r.tb_ao_meeting_agenda_ag_id === a.ag_id)
        return (
          <div key={a.ag_id} style={{ marginTop: 6 }}>
            <div style={{ fontWeight: 'bold' }}>วาระที่ {a.ag_order}&nbsp;&nbsp;{a.ag_title}</div>
            {a.ag_detail && <div className="doc-indent" style={{ whiteSpace: 'pre-wrap' }}>{a.ag_detail}</div>}
            {resolutions.map(r => (
              <div key={r.ms_id} className="doc-indent">
                <b>มติ:</b> {r.ms_text}
                {r.owner_name ? ` (ผู้รับผิดชอบ: ${r.owner_name}${r.ms_due ? `, กำหนดเสร็จ ${r.ms_due}` : ''})` : ''}
              </div>
            ))}
          </div>
        )
      })}
      {/* มติที่ไม่ผูกวาระ */}
      {meeting.resolutions.filter(r => !r.tb_ao_meeting_agenda_ag_id).length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontWeight: 'bold' }}>มติอื่น ๆ</div>
          {meeting.resolutions.filter(r => !r.tb_ao_meeting_agenda_ag_id).map(r => {
            const rs = RESOLUTION_STATUS[r.ms_status]
            return (
              <div key={r.ms_id} className="doc-indent">
                <b>มติ:</b> {r.ms_text}{r.owner_name ? ` (ผู้รับผิดชอบ: ${r.owner_name})` : ''}{rs ? ` [${rs.label}]` : ''}
              </div>
            )
          })}
        </div>
      )}

      <table style={{ width: '100%', marginTop: 40 }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', textAlign: 'center' }}>
              <div>ลงชื่อ ................................................</div>
              <div style={{ marginTop: 6 }}>(....................................)</div>
              <div>ผู้จดรายงานการประชุม</div>
            </td>
            <td style={{ textAlign: 'center' }}>
              <div>ลงชื่อ ................................................</div>
              <div style={{ marginTop: 6 }}>(....................................)</div>
              <div>ประธานการประชุม</div>
            </td>
          </tr>
        </tbody>
      </table>
    </OfficialDocLayout>
  )
}
